import React, { useState } from "react";
import { styled } from "@mui/system";
import { useDispatch } from "react-redux";
import {
  setContactChangeType,
  toggleContactChangeModal,
  useContactChangeState,
  BANNER_STAGES,
} from "src/store/features/contactChange.slice";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import useContactChange from "./useContactChange";
import { useUserSlice } from "src/store/features/user.slice";
import { getSessionItem } from "src/utils/SessionManagement";

export const ContactChangeStyledButton = styled("div")<{ disabled?: boolean }>(({ disabled }) => ({
  display: "flex",
  padding: "8px 12px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  borderRadius: "20px",
  border: disabled ? "1px solid #9CA3A8" : "1px solid #E5E9EB",
  background: "#FFF",
  cursor: disabled ? "not-allowed" : "pointer",
  color: disabled ? "#9CA3A8" : "#002035",
  fontFamily: "Roobert, sans-serif",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "20px",
  whiteSpace: "nowrap",
  ...(!disabled && {
    "&:hover": {
      background: "#F8F9F9",
    },
  }),
}));

const TooltipContainer = styled("div")<{ isVisible: boolean; isMobile: boolean }>(
  ({ isVisible, isMobile }) => ({
    visibility: isVisible ? "visible" : "hidden",
    textAlign: "center",
    position: "absolute",
    zIndex: 1,
    bottom: "125%",
    left: "50%",
    transform: isMobile ? "translateX(-60%)" : "translateX(-50%)",
    opacity: isVisible ? 1 : 0,
    transition: "opacity 0.3s",

    display: "flex",
    width: "auto",
    maxWidth: "90vw",
    minWidth: "240px",
    padding: "12px 16px",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    borderRadius: "10px",
    background: "rgba(0, 32, 53, 0.80)",
    alignSelf: "stretch",
    color: "#FFF",
    fontFamily: "Söhne",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "14px",
  }),
);

const TooltipWrapper = styled("div")({
  position: "relative",
  display: "inline-block",
});

interface ContactChangeButtonProps {
  type: "email" | "phone";
}

const ContactChangeButton: React.FC<ContactChangeButtonProps> = ({ type }) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const { is2FAEnabled } = useUserSlice();

  const { bannerStage, contactChangeType, changingValueName, secondaryAuthValueName } =
    useContactChangeState();
  const { latestContactChangeLoading } = useContactChange();

  const [isHovered, setIsHovered] = useState(false);

  let buttonText = type === "email" ? "Change email" : "Change phone";
  if (bannerStage === BANNER_STAGES.IN_PROGRESS) {
    if (contactChangeType === type) {
      buttonText = "In progress";
    }
  } else if (bannerStage === BANNER_STAGES.ACTION_REQUIRED) {
    if (contactChangeType === type) {
      buttonText = "Action required";
    }
  }

  const getTooltipMessage = () => {
    if (bannerStage === BANNER_STAGES.COMPLETED) {
      return `There are limitations currently placed on your account for 24 hours after the change was processed. You cannot change your contact details during this time.`;
    }

    if (type === contactChangeType) {
      if (bannerStage === BANNER_STAGES.IN_PROGRESS) {
        return `Contact change in progress.`;
      } else if (bannerStage === BANNER_STAGES.ACTION_REQUIRED) {
        return `Your request to change ${contactChangeType} is incomplete, please continue above to finalise changes.`;
      }
    } else {
      if (bannerStage === BANNER_STAGES.IN_PROGRESS) {
        return `You can only change one contact detail at a time. Please wait until your ${changingValueName} change is complete.`;
      } else if (bannerStage === BANNER_STAGES.ACTION_REQUIRED) {
        return `You can only change one contact detail at a time. If you would like to change your ${secondaryAuthValueName} now, click on "Cancel changes" above. Alternatively, you can complete your ${contactChangeType} change.`;
      }
    }

    if (!is2FAEnabled) {
      return `You must enable 2FA to change your ${type}`;
    }

    return "";
  };

  // Enable email button if type is email and 2FA is enabled
  const enableEmailChange = type === "email" && is2FAEnabled;

  // Enable phone button if type is phone feature
  const enablePhoneChange = type === "phone";

  // Change in progress or loading
  const changeInProgressOrLoading = bannerStage !== null || latestContactChangeLoading;

  // not in progress or loading, and either email or phone change is enabled
  const isButtonEnabled = !changeInProgressOrLoading && (enablePhoneChange || enableEmailChange);

  // If button is disabled, show tooltip
  const isVisible = !isButtonEnabled && isHovered;

  // if button is not disabled, show tooltip
  const handleButtonClick = (action: () => void) => {
    if (isButtonEnabled) {
      action();
    }
  };

  return (
    <TooltipWrapper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <ContactChangeStyledButton
        disabled={!isButtonEnabled}
        onClick={() =>
          handleButtonClick(() => {
            dispatch(toggleContactChangeModal()); // open modal
            dispatch(setContactChangeType(type));
          })
        }>
        {buttonText}
      </ContactChangeStyledButton>
      <TooltipContainer isVisible={isVisible} isMobile={isMobile}>
        {getTooltipMessage()}
      </TooltipContainer>
    </TooltipWrapper>
  );
};

export default ContactChangeButton;
